import { ActionIcon, Stack, Text, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Role, TruckSimple, useDeleteTruckBrandMutation, useGetTruckBrandsQuery } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_trucks/caminhoes/marcas/")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Marcas de Caminhões" }] }),
  beforeLoad: requireRoles({
    allowed: [Role.Administrador]
  })
});

function RouteComponent() {
  const { data = [], isFetching, refetch } = useGetTruckBrandsQuery();
  const [deleteTruckBrand, { isLoading: isDeleting }] = useDeleteTruckBrandMutation();

  const columns: GridColDef<TruckSimple>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
      align: "center",
      headerAlign: "center",
      type: "number"
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      type: "string"
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      renderCell: ({ row }) => {
        const { id, name } = row;

        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Marca">
              <ActionIcon variant="light" color="yellow" onClick={() => handleEdit({ id, brand: name })}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Excluir Marca">
              <ActionIcon variant="light" color="red" onClick={() => handleDelete({ id })}>
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  const handleEdit = (body: { id: number; brand: string }) => {
    MODAL_HANDLER.createEdit.truck.brand(body);
  };

  const handleDelete = (body: { id: number }) => {
    modals.openConfirmModal({
      title: "Excluir Marca",
      children: (
        <Stack className="text-center" gap="xs">
          <Title order={4}>Tem certeza que deseja excluir esta marca?</Title>
          <Text c="dimmed">Esta ação não pode ser desfeita.</Text>
        </Stack>
      ),
      onConfirm: () => deleteTruckBrand(body),
      cancelProps: { disabled: isDeleting },
      confirmProps: { loading: isDeleting, color: "red" },
      groupProps: { className: cn("grid! grid-cols-1 gap-[inherit] md:grid-cols-2") },
      labels: { confirm: "Deletar", cancel: "Cancelar" }
    });
  };

  return (
    <Stack>
      <div className="grid grid-cols-[1fr_auto] gap-[inherit]">
        <DashboardComponents.Title>Marcas de Caminhões</DashboardComponents.Title>

        <div className="flex items-center justify-center gap-2">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Marcas" }} onClick={refetch} loading={isFetching} />

          <Tooltip label="Cadastrar nova Marca">
            <ActionIcon variant="filled" size="xl" onClick={() => MODAL_HANDLER.createEdit.truck.brand()}>
              <PlusIcon />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      <DataGridBase rows={data} columns={columns} loading={isFetching} />
    </Stack>
  );
}
